import { Stack, Typography, LinearProgress, Chip, Box } from "@mui/material";
import { colors, shadows } from "./source.styles";

// Capitalize first letter of each word
const capitalizeWords = (str) => {
  if (!str) return str;
  return str
    .split(/[_\s-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const QualityItem = ({ rank, name, score, maxScore, status, statusColor, statusBgColor, progressColor }) => {
  const progress = (score / maxScore) * 100;

  return (
    <Box sx={{ mb: 3, position: "relative" }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {/* Rank + Name + Progress (left side) */}
        <Stack spacing={1} sx={{ flex: 1 }}>
          {/* Name row */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: colors.textMuted,
                minWidth: 20,
              }}
            >
              {String(rank).padStart(2, "0")}
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: colors.textPrimary,
              }}
            >
              {capitalizeWords(name)}
            </Typography>
          </Stack>

          {/* Progress row */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ pl: 3.75 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                width: "50%",
                height: 8,
                borderRadius: 999,
                bgcolor: colors.borderLight,
                "& .MuiLinearProgress-bar": {
                  bgcolor: progressColor || colors.primary,
                  borderRadius: 999,
                },
              }}
            />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: colors.textPrimary,
              }}
            >
              {score}/{maxScore}
            </Typography>
          </Stack>
        </Stack>

        {/* Status badge (right side, vertically centered) */}
        <Chip
          label={status}
          sx={{
            height: 24,
            fontSize: 11,
            fontWeight: 700,
            bgcolor: statusBgColor || "#F3F4F6",
            color: statusColor || colors.textPrimary,
            textTransform: "capitalize",
            border: "none",
            flexShrink: 0,
            "& .MuiChip-label": {
              px: 1.5,
            },
          }}
        />
      </Stack>
    </Box>
  );
};

const SourceQualityLeaderboard = ({ data = [] }) => {
  const maxScore = 100;

  const getStatusStyles = (status) => {
    const statusMap = {
      "excellent": {
        color: "#059669",
        bgcolor: "#ECFDF5",
        progressColor: colors.success,
      },
      "good": {
        color: "#0284C7",
        bgcolor: "#E0F2FE",
        progressColor: colors.chartBlue,
      },
      "average": {
        color: "#D97706",
        bgcolor: "#FEF3C7",
        progressColor: colors.chartOrange,
      },
      "poor": {
        color: "#DC2626",
        bgcolor: "#FEE2E2",
        progressColor: colors.danger,
      },
    };
    return (
      statusMap[status?.toLowerCase()] || {
        color: colors.textPrimary,
        bgcolor: "#F3F4F6",
        progressColor: colors.primary,
      }
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          color: colors.textMuted,
          textTransform: "uppercase",
          mb: 3,
        }}
      >
        Source Quality Leaderboard
      </Typography>

      {data.map((item, index) => {
        const styles = getStatusStyles(item.status);
        return (
          <QualityItem
            key={index}
            rank={index + 1}
            name={item.name}
            score={item.score}
            maxScore={maxScore}
            status={item.status}
            statusColor={styles.color}
            statusBgColor={styles.bgcolor}
            progressColor={styles.progressColor}
          />
        );
      })}
    </Box>
  );
};

export default SourceQualityLeaderboard;
